import { Button, Card, Col, Divider, Row, Tag } from 'antd';
import clsx from 'clsx';
import { isNaN } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../AppContext';
import { ForwardArrow } from '../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../common/CommonUseLazyQuery';
import {
  BREAKPOINTS,
  INS_STATUS_CLASSNAME,
  INS_STATUS_KEYS,
  INS_STATUS_LABEL,
  ROUTES,
} from '../../../../common/constants';
import useRouter from '../../../../common/useRouter';
import LoaderComponent from '../../../../components/LoaderComponent';
import Error404 from '../../../../Error404';
import history from '../../../../historyData';
import { GET_DETAILS } from '../graphql/Queries';
import AddResponseDrawer from './AddResponseDrawer';
import AlreadyRespondedModal from './AlreadyRespondedModal';
import InstructionMoreDetails from './InstructionMoreDetails';
import InstructionSummary from './InstructionSummary';
import IssueActivity from './IssueActivity';
import RecommendationDetails from './RecommendationDetails';
import ShowDetailsDrawer from './ShowDetailsDrawer';

const Details = ({ token }) => {
  const { navigate, location } = useRouter();
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const tenantRecord = location?.state?.tenantRecord;
  const projectRecord = location?.state?.projectRecord;
  const currentPath = location?.state?.currentPath;
  const [showRespondedModal, setShowRespondedModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [loading, setLoading] = useState(true);
  const { dispatch, changeTenant } = useContext(AppContext);

  useEffect(() => {
    const unlisten = history.listen((props) => {
      if (props.action === 'POP') {
        navigate(currentPath, {
          state: {
            navFilter,
            navPagination,
            tenantRecord,
            projectRecord,
          },
        });
      }
    });
    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, []);

  const [fetchDetails, { data }] = useLazyQueryWithCallbacks(GET_DETAILS, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      dispatch({
        type: 'SET_INSTRUCTION_DETAIL',
        data: JSON.stringify(res?.getInstructionForUser),
      });
      setLoading(false);
    },
    onError() {
      navigate(ROUTES.MAIN);
    },
  });
  const { id, tenantId } = useParams();

  useEffect(() => {
    if (tenantId) {
      changeTenant(tenantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (!isNaN(Number(id))) {
      fetchDetails({ variables: { id, token } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const instructionData = data?.getInstructionForUser;

  const refetch = () => {
    fetchDetails({ variables: { id, token } });
  };

  const status = instructionData?.status;
  useEffect(() => {
    if (status === INS_STATUS_KEYS.RESPONDED) {
      setShowRespondedModal(true);
    }
  }, [status]);

  if (isNaN(Number(id))) {
    return <Error404 />;
  }
  if (loading) {
    return <LoaderComponent />;
  }
  return (
    <div className="respond-detail" id="issue-details-container">
      <div
        id="issue-details-wrapper"
        className={clsx(
          'project-details',
          token && isDesktopViewport && 'p-20',
        )}
      >
        <div className="project-details-title justify-between page-header">
          <div className="project-details-title-text d-flex align-center">
            <h1
              id="issue-details-back-button"
              className="pointer mr-10"
              onClick={() => {
                navigate(
                  token ? ROUTES.LOGIN : `${ROUTES.TODO}${ROUTES.ISSUES}`,
                  {
                    state: {
                      navFilter,
                      navPagination,
                      tenantRecord,
                      projectRecord,
                    },
                  },
                );
              }}
            >
              Issues
            </h1>
            <ForwardArrow id="issue-details-arrow" />
            <div
              className="h1-typography issue-heading ellipsis-text"
              id="issue-details-name"
            >
              {instructionData?.name}
            </div>
            <Tag
              id="issue-details-status-tag"
              className={`${
                INS_STATUS_CLASSNAME[instructionData?.status]
              } ml-10 status-tag`}
            >
              {INS_STATUS_LABEL[instructionData?.status]}
            </Tag>
          </div>
        </div>
        <div className="mobile-card-wrapper" id="issue-details-card-wrapper">
          <Card
            className="issue-detail-card"
            variant="borderless"
            id="issue-details-card"
          >
            <Row
              className={clsx(
                'issue-detail-layout',
                token && 'issue-detail-public-layout',
              )}
              gutter={24}
            >
              <Col className="fixed-height" span={isDesktopViewport ? 8 : 24}>
                <Card
                  className="instruction-tab-details-summary"
                  id="issue-details-summary-card"
                >
                  <InstructionMoreDetails instructionData={instructionData} />
                  {!isDesktopViewport && (
                    <>
                      <Button
                        id="issue-details-view-button"
                        type="link"
                        className="view-details"
                        onClick={() => setShowDetails(true)}
                      >
                        View Details
                      </Button>
                      {!isDesktopViewport &&
                        instructionData?.status === INS_STATUS_KEYS?.RAISED && (
                          <div
                            className="add-response-button"
                            id="issue-details-add-response-wrapper"
                          >
                            <Button
                              id="issue-details-add-response-button"
                              className="width-percent-100"
                              shape="round"
                              type="primary"
                              onClick={() => setShowResponseModal(true)}
                            >
                              Add Response
                            </Button>
                          </div>
                        )}
                    </>
                  )}
                  {isDesktopViewport && (
                    <>
                      <Divider />
                      <InstructionSummary instructionData={instructionData} />
                    </>
                  )}
                  {instructionData?.instructionHistory?.length > 0 && (
                    <>
                      <Divider />
                      <RecommendationDetails data={instructionData} />
                    </>
                  )}
                </Card>
                {!isDesktopViewport && (
                  <Card
                    id="issue-details-activity-mobile-card"
                    variant="borderless"
                    className={clsx(
                      'issue-activity-mobile',
                      instructionData?.status === INS_STATUS_KEYS?.RAISED &&
                        'raised-issues',
                    )}
                  >
                    <IssueActivity
                      instructionData={instructionData}
                      token={token}
                      refetch={refetch}
                    />
                  </Card>
                )}
              </Col>
              {isDesktopViewport && (
                <Col
                  className="fixed-height"
                  span={isDesktopViewport ? 16 : 24}
                >
                  <Card
                    className="height-percent-100 overflow-auto activity-card"
                    id="issue-details-activity-card"
                  >
                    <IssueActivity
                      instructionData={instructionData}
                      token={token}
                      refetch={refetch}
                    />
                  </Card>
                </Col>
              )}
            </Row>
          </Card>
        </div>
      </div>
      {showDetails && (
        <ShowDetailsDrawer
          id="issue-details-show-drawer"
          instructionData={instructionData}
          open={showDetails}
          onClose={() => setShowDetails(false)}
        />
      )}
      {showResponseModal && (
        <AddResponseDrawer
          open={showResponseModal}
          instructionData={instructionData}
          onClose={() => setShowResponseModal(false)}
          token={token}
          refetch={refetch}
        />
      )}
      {showRespondedModal && (
        <AlreadyRespondedModal
          showModal={showRespondedModal}
          setShowModal={setShowRespondedModal}
          data={{
            respondedOn: instructionData?.respondedOn,
            responder: instructionData?.responderUser,
            instructionData,
          }}
        />
      )}
    </div>
  );
};

export default Details;
